import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowUpRight, MousePointerClick } from 'lucide-react'
import { cn } from '@/lib/utils'

export const BANNER_AUTO_SLIDE_MS = 5000

export function bannerHref(link?: string) {
  const href = link?.trim()
  return href || null
}

export function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href)
}

export function BannerTapHint() {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) {
    return (
      <span
        className={cn(
          'pointer-events-none absolute bottom-4 right-4 z-10',
          'inline-flex items-center gap-1.5 rounded-full',
          'border border-white/20 bg-black/50 px-3 py-1.5',
          'text-[11px] font-medium uppercase tracking-wide text-white/90 backdrop-blur-md'
        )}
        aria-hidden
      >
        <MousePointerClick className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">Click to open</span>
      </span>
    )
  }

  return (
    <span className="pointer-events-none absolute bottom-4 right-4 z-10" aria-hidden>
      <motion.span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full',
          'border border-white/25 bg-black/55 px-3 py-1.5',
          'text-[11px] font-medium uppercase tracking-wide text-white/90 backdrop-blur-md'
        )}
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <MousePointerClick className="h-3.5 w-3.5 shrink-0" />
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">Click to open</span>
      </motion.span>
    </span>
  )
}

export function BannerLinkWrap({
  href,
  children,
  label,
  className,
}: {
  href: string | null
  children: ReactNode
  label: string
  className?: string
}) {
  const wrapClass = cn(
    'group relative block h-full w-full overflow-hidden',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/50',
    href && 'cursor-pointer',
    className
  )

  if (!href) {
    return <div className={wrapClass}>{children}</div>
  }

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className={wrapClass}
      >
        {children}
        <BannerTapHint />
      </a>
    )
  }

  return (
    <Link to={href} aria-label={label} className={wrapClass}>
      {children}
      <BannerTapHint />
    </Link>
  )
}
